import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import logo from "@/assets/cryptofellas.svg";

export default function Login() {
  const { signIn, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState("");

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate("/home");
    }
  }, [user, navigate]);

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);

    if (value && !validateEmail(value)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    setAuthError("");

    const { error } = await signIn(email, password);

    if (error) {
      setAuthError(error.message);
      setIsLoading(false);
    } else {
      // Successful login - user state will update and trigger redirect
      setIsLoading(false);
    }
  };

  const isFormValid = email && validateEmail(email) && !emailError && password;

  return (
    <div>
      {/* Header */}
      <Header showLogo={false} showBack={false} />

      <div className="p-5">
        <div className="w-full max-w-md mx-auto flex flex-col items-center gap-6 md:gap-12">
          {/* Logo Section */}
          <div className="w-full rounded-lg mt-6 md:mt-12 mb-12 md:mb-16">
            <div className="mt-8">
              <img src={logo} alt="FellasCard Logo" className="h-auto w-full" />
              <div className="text-2xl font-bold text-muted-foreground mt-2">
                Membership Card
              </div>
            </div>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="w-full space-y-5">
            <div className="space-y-1">
              <Label
                htmlFor="email"
                className="text-xs text-muted-foreground font-normal"
              >
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder=""
                value={email}
                onChange={handleEmailChange}
                className="h-12 px-4 bg-transparent border-0 border-b rounded-none placeholder:text-muted-foreground text-sm focus-visible:ring-0"
                required
              />
              {emailError && (
                <p className="text-[10px] text-red-500 mt-1">{emailError}</p>
              )}
            </div>

            <div className="space-y-1">
              <Label
                htmlFor="password"
                className="text-xs text-muted-foreground font-normal"
              >
                Password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder=""
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 px-4 bg-transparent border-0 border-b rounded-none placeholder:text-muted-foreground text-sm focus-visible:ring-0"
                required
              />
            </div>

            {authError && (
              <div className="p-3">
                <p className="text-xs text-red-600 dark:text-red-400">
                  {authError}
                </p>
              </div>
            )}

            <Button
              type="submit"
              disabled={!isFormValid || isLoading}
              className="w-full bg-primary hover:bg-primary/90 text-white font-bold h-12 mt-8 md:mt-12 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Logging in..." : "Log in"}
            </Button>
          </form>

          {/* Links Section */}
          <div className="flex flex-col items-center gap-5">
            <div className="flex items-center gap-2.5">
              <span className="text-sm text-foreground/80">
                Don't have an account?
              </span>
              <Link
                to="/signup"
                className="text-sm font-bold text-primary/80 hover:text-primary hover:underline"
              >
                Sign up
              </Link>
            </div>
            <div className="flex items-center gap-2.5">
              <span className="text-sm text-foreground/80">
                Forgot your password?
              </span>
              <Link
                to="/forgot-password"
                className="text-sm font-bold text-orange-500 hover:text-orange-600 hover:underline"
              >
                Forgot password
              </Link>
            </div>
          </div>

          <Footer />
        </div>
      </div>
    </div>
  );
}
